"use client"
import { useTranslations } from "next-intl"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { DialogCard } from "@/components/ui/dialog-card"
import { DockSheet } from "@/components/ui/dock-sheet"
import { useMediaQuery } from "@/hooks/use-media-query"
import { FolderPlus, Plus } from "lucide-react"
import {
  creationFlowPrimaryCtaClass,
  creationFlowButtonOutlineClass,
  creationFlowInputClass,
  creationFlowLabelClass,
  creationFlowTextareaClass,
} from "@/lib/creation-flow-form-styles"

interface CreateCollectionDialogProps {
  selectedListingIds: string[]
  onCreateCollectionAction: (title: string, description: string, listingIds: string[]) => void
  isCreating?: boolean
  triggerClassName?: string
  triggerText?: string
  stackAboveDialog?: boolean
  allowEmptySelection?: boolean
  triggerIcon?: "folder" | "plus"
  open?: boolean
  onOpenChange?: (open: boolean) => void
  hideTrigger?: boolean
}

export function CreateCollectionDialog({
  selectedListingIds,
  onCreateCollectionAction,
  isCreating = false,
  triggerClassName,
  triggerText = "",
  stackAboveDialog = false,
  allowEmptySelection = false,
  triggerIcon = "folder",
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}: CreateCollectionDialogProps) {
  const tColl = useTranslations("collections");
  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) {
      setInternalOpen(next)
    }
    onOpenChange?.(next)
  }
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  const isDesktop = useMediaQuery("(min-width: 768px)")

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (title.trim()) {
      onCreateCollectionAction(title.trim(), description.trim(), selectedListingIds)
      setTitle("")
      setDescription("")
      setOpen(false)
    }
  }

  const formContent = (
    <form id="create-collection-form-desktop" onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="title-desktop" className={creationFlowLabelClass}>{tColl("collectionName")}</label>
          <Input
            id="title-desktop"
            placeholder={tColl("collectionNamePlaceholder")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={creationFlowInputClass}
            autoFocus={isDesktop}
            onFocus={(e) => {
              e.preventDefault()
              const scrollY = window.scrollY
              setTimeout(() => {
                window.scrollTo(0, scrollY)
                e.target.focus()
              }, 0)
            }}
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="description-desktop" className={creationFlowLabelClass}>{tColl("descriptionLabel")}</label>
          <Textarea
            id="description-desktop"
            placeholder={tColl("descriptionPlaceholderCreate")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={creationFlowTextareaClass + " min-h-[100px]"}
            onFocus={(e) => {
              e.preventDefault()
              const scrollY = window.scrollY
              setTimeout(() => {
                window.scrollTo(0, scrollY)
                e.target.focus()
              }, 0)
            }}
          />
        </div>
      </div>
    </form>
  )

  return (
    <>
      {!hideTrigger ? (
        <Button
          variant="outline"
          size="sm"
          disabled={!allowEmptySelection && selectedListingIds.length === 0}
          className={triggerClassName || "h-10 w-full border-dashed"}
          onClick={() => setOpen(true)}
        >
          {triggerIcon === "plus" ? (
            <Plus className="h-4 w-4 mr-2" />
          ) : (
            <FolderPlus className="h-4 w-4 mr-2" />
          )}
          {triggerText}
        </Button>
      ) : null}

      {!isDesktop && (
        <DockSheet
          open={open}
          onOpenChange={setOpen}
          title={tColl("createTitle")}
          footer={
            <Button
              type="submit"
              form="create-collection-form"
              disabled={!title.trim() || isCreating}
              className={creationFlowPrimaryCtaClass}
            >
              {isCreating ? tColl("creating") : tColl("createBtn")}
            </Button>
          }
        >
          <form id="create-collection-form" onSubmit={handleSubmit} className="space-y-6">
            {selectedListingIds.length > 0 ? (
              <p className="text-sm text-muted-foreground pb-2">
                {tColl("createNewWithItems", { count: selectedListingIds.length })}
              </p>
            ) : null}
            <div className="space-y-4">
              <div className="space-y-2">
                <Input
                  id="title"
                  placeholder={tColl("collectionNamePlaceholder")}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={creationFlowInputClass}
                  autoFocus={false}
                />
              </div>
              <div className="space-y-2">
                <Textarea
                  id="description"
                  placeholder={tColl("descriptionPlaceholderOptional")}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={creationFlowTextareaClass + " min-h-[100px]"}
                />
              </div>
            </div>
          </form>
        </DockSheet>
      )}

      {isDesktop && (
        <DialogCard
          open={open}
          onOpenChange={setOpen}
          nested={stackAboveDialog}
          title={tColl("createTitle")}
          belowTitle={
            selectedListingIds.length > 0 ? (
              <p className="text-center text-sm text-muted-foreground">
                {tColl("createNewWithItems", { count: selectedListingIds.length })}
              </p>
            ) : null
          }
          panelClassName="max-w-2xl h-[80dvh]"
          footer={
            <Button
              type="submit"
              form="create-collection-form-desktop"
              disabled={!title.trim() || isCreating}
              className={creationFlowPrimaryCtaClass}
            >
              {isCreating ? tColl("creating") : tColl("createBtn")}
            </Button>
          }
        >
          {formContent}
        </DialogCard>
      )}
    </>
  )
}
